import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logService } from '../../lib/logService';

// platform-loading-infinito.2 — Recovery UX for loading gates.
// Consumer-gated (Pax iter 1 fix D1): only mounted when consumer detects loading=true.
// Pattern: useEffect cleanup-cancels (no functional update closure).

const RECOVERY_THRESHOLD_MS = 8000;

interface LoadingGateRecoveryProps {
  gateName: 'subscription-gate' | 'protected-route';
  spinnerVariant: 'loader2' | 'border-spin';
}

export const LoadingGateRecovery: React.FC<LoadingGateRecoveryProps> = ({ gateName, spinnerVariant }) => {
  const [showRecovery, setShowRecovery] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // AC1: setTimeout(8s) + cleanup-cancels.
  // Telemetria emitida dentro do callback (AC5(a)) — só dispara quando UI aparece.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowRecovery(true);
      logService.track('platform.loading.recovery_shown', {
        gate: gateName,
        threshold_ms: RECOVERY_THRESHOLD_MS,
        version: __APP_VERSION__,
      });
    }, RECOVERY_THRESHOLD_MS);
    return () => clearTimeout(timeoutId);
  }, [gateName]);

  // AC2: "Tentar novamente" → telemetry + window.location.reload().
  const handleReload = () => {
    if (isReloading || isClearing) return;
    setIsReloading(true);
    logService.track('platform.loading.recovery_action', {
      gate: gateName,
      action: 'reload',
      version: __APP_VERSION__,
    });
    window.location.reload();
  };

  // AC3: "Limpar sessão" → 3-layer defensive recovery.
  const handleClearSession = async () => {
    if (isReloading || isClearing) return;
    setIsClearing(true);
    logService.track('platform.loading.recovery_action', {
      gate: gateName,
      action: 'clear_session',
      version: __APP_VERSION__,
    });
    // Camada 1: local signOut (no API call — safe even if Supabase client is in bad state).
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    // Camada 2: manual nuke sb-* localStorage keys (defensive vs Camada 1 leftover).
    // try/catch handles private browsing where localStorage may throw.
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // private browsing — Camada 3 still runs
    }
    // Camada 3: hard navigation — nukes React state in memory.
    window.location.href = '/auth/login';
  };

  const spinner =
    spinnerVariant === 'loader2' ? (
      <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
    ) : (
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    );

  // AC4(c): markup <8s é byte-identical ao do gate original (snapshot determinístico).
  if (!showRecovery) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        {spinner}
      </div>
    );
  }

  // AC4(d): >8s — spinner acima, painel abaixo, layout flex-col.
  return (
    <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center gap-6 px-4">
      {spinner}
      <div className="max-w-sm w-full rounded-xl border border-neutral-800 bg-neutral-900/50 backdrop-blur p-6 text-center">
        <p className="text-sm text-neutral-300 mb-4">Está demorando mais do que o normal...</p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleReload}
            disabled={isReloading || isClearing}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            {isReloading ? 'Recarregando...' : 'Tentar novamente'}
          </button>
          <button
            type="button"
            onClick={handleClearSession}
            disabled={isReloading || isClearing}
            className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-60 disabled:cursor-not-allowed text-neutral-300 rounded-lg text-sm transition-colors"
          >
            {isClearing ? 'Limpando...' : 'Limpar sessão e fazer login'}
          </button>
        </div>
      </div>
    </div>
  );
};
